import { useCallback, useEffect, useRef, useState, type HTMLAttributes, type MutableRefObject, type ReactNode, type Ref } from 'react';
import { Icon } from './Icon';

/**
 * A row that scrolls sideways — style filters, material categories, model parts.
 *
 * A finger drags it and a trackpad swipes it, but a desktop mouse has neither gesture and
 * a row with a hidden scrollbar simply cannot be moved. So the wheel moves it, and a step
 * button sits over each end while there is more content that way. The buttons are a
 * pointer affordance only — the row's own controls stay reachable by Tab and the arrow
 * keys — so they never take focus and stay out of the accessibility tree.
 *
 * Everything passed through (`role`, `aria-label`, `onKeyDown`, the class) lands on the
 * scrolling element itself, so a caller's radio group keeps its own semantics.
 */
export function HorizontalStrip({ className, shellClassName, scrollerRef, children, ...rest }: {
  className?: string;
  /** Class for the wrapper, which is what a flex parent lays out. */
  shellClassName?: string;
  /** The scrolling element, for a caller that scrolls it itself. */
  scrollerRef?: Ref<HTMLDivElement>;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  const shell = useRef<HTMLDivElement | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);
  /** Both true is also the no-overflow case: nothing beyond either edge, so no step buttons. */
  const [ends, setEnds] = useState({ start: true, end: true });

  const attach = (element: HTMLDivElement | null) => {
    strip.current = element;
    if (typeof scrollerRef === 'function') scrollerRef(element);
    else if (scrollerRef) (scrollerRef as MutableRefObject<HTMLDivElement | null>).current = element;
  };

  const measure = useCallback(() => {
    const element = strip.current;
    if (!element) return;
    const last = Math.max(0, element.scrollWidth - element.clientWidth);
    setEnds((was) => {
      const next = { start: element.scrollLeft <= 1, end: element.scrollLeft >= last - 1 };
      return was.start === next.start && was.end === next.end ? was : next;
    });
  }, []);

  useEffect(() => {
    const element = strip.current;
    if (!element) return;
    measure();
    // The row grows with its content (categories arriving, a project with more parts), and
    // the shell is what a resize reaches, so watch both the box and what is in it.
    const resizes = new ResizeObserver(measure);
    resizes.observe(element);
    const changes = new MutationObserver(measure);
    changes.observe(element, { childList: true, subtree: true });
    /**
     * A mouse only sends `deltaY`. Nothing here scrolls vertically, so that turn belongs to
     * this row — but only while it can still move: at either end the event is left alone and
     * the page behind keeps it. A trackpad's sideways swipe already arrives as `deltaX` and
     * is not touched. The listener sits on the shell so a turn over a step button counts too.
     */
    const wheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const by = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * element.clientWidth : event.deltaY;
      const before = element.scrollLeft;
      element.scrollLeft = before + by;
      if (element.scrollLeft !== before) event.preventDefault();
    };
    const wheelTarget = shell.current ?? element;
    wheelTarget.addEventListener('wheel', wheel, { passive: false });
    element.addEventListener('scroll', measure, { passive: true });
    return () => {
      resizes.disconnect();
      changes.disconnect();
      wheelTarget.removeEventListener('wheel', wheel);
      element.removeEventListener('scroll', measure);
    };
  }, [measure]);

  const step = (direction: 1 | -1) => {
    const element = strip.current;
    if (!element) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    element.scrollBy({ left: direction * Math.max(120, element.clientWidth * .8), behavior });
  };

  return (
    <div ref={shell} className={`strip-shell${shellClassName ? ` ${shellClassName}` : ''}`} data-start={ends.start} data-end={ends.end}>
      <div {...rest} ref={attach} className={`strip${className ? ` ${className}` : ''}`}>{children}</div>
      {!ends.start && <button type="button" className="strip-step" data-side="start" tabIndex={-1} aria-hidden="true" onClick={() => step(-1)}><Icon name="back" size={17} /></button>}
      {!ends.end && <button type="button" className="strip-step" data-side="end" tabIndex={-1} aria-hidden="true" onClick={() => step(1)}><Icon name="next" size={17} /></button>}
    </div>
  );
}
